/**
 * WorkflowRunGraphLive — self-contained live DAG graph viewer.
 *
 * Accepts a workflow `definition` and an optional `runId`.
 * Internally derives the graph layout, subscribes to the run stream +
 * job-progress stream, and renders `WorkflowGraphView` with full live state.
 *
 * Extracted so it can be reused in both the Runner tab and the Scopes
 * "New Scope" accordion (DRY).
 */
import React, { useEffect, useMemo, useState } from 'react';

import { coerceObject, computeTreeLayoutVertical, formatJson, fromDag } from '@/features/system/orchestrator/components/workflowBuilder/layout';
import WorkflowGraphView from '@/features/system/orchestrator/components/workflowBuilder/WorkflowGraphView';
import { useWorkflowRunStream } from '@/features/system/orchestrator/hooks/useWorkflowRunStream';
import { useJobProgressStream } from '@/features/system/jobs/hooks/useJobProgressStream';

/* ── Props ─────────────────────────────────────────────────────── */

export interface WorkflowRunGraphLiveProps {
  /** Raw workflow definition object (contains dag). */
  definition: unknown;
  /** Active run ID — when set, graph shows live step status + progress. */
  runId?: string | null;
}

/* ── Component ─────────────────────────────────────────────────── */

export default function WorkflowRunGraphLive({ definition, runId }: WorkflowRunGraphLiveProps) {
  /* ── 1. Parse graph from definition ──────────────────────────── */
  const graph = useMemo(() => {
    if (!definition) return null;
    try {
      const parsed = fromDag(definition);
      const inferredEdges = inferEdgesFromBindings(parsed.nodes, parsed.edges);
      const allEdges = [...parsed.edges, ...inferredEdges];
      const layout = computeTreeLayoutVertical(parsed.nodes, allEdges);

      const inSchema = coerceObject(parsed.inputsSchema);
      const inProps = coerceObject(inSchema.properties);
      const inputKeys = Object.keys(inProps).sort();
      const inputMetaByKey: Record<string, { type?: string; description?: string }> = {};
      for (const k of inputKeys) {
        const p = coerceObject(inProps[k]);
        inputMetaByKey[k] = {
          type: typeof p.type === 'string' ? p.type : undefined,
          description: typeof p.description === 'string' ? p.description : undefined,
        };
      }

      const outSchema = coerceObject(parsed.outputsSchema);
      const outProps = coerceObject(outSchema.properties);
      const outputKeys = Object.keys(outProps).sort();
      const outputMetaByKey: Record<string, { type?: string; description?: string }> = {};
      for (const k of outputKeys) {
        const p = coerceObject(outProps[k]);
        outputMetaByKey[k] = {
          type: typeof p.type === 'string' ? p.type : undefined,
          description: typeof p.description === 'string' ? p.description : undefined,
        };
      }

      return {
        nodes: parsed.nodes,
        edges: allEdges,
        layout,
        inputKeys,
        inputMetaByKey,
        outputKeys,
        outputMetaByKey,
        outputsBindingsJson: formatJson(parsed.outputsBindings),
      };
    } catch {
      return null;
    }
  }, [definition]);

  /* ── 2. Run stream ───────────────────────────────────────────── */
  const activeRunId = runId ?? null;
  const { snapshot: runSnapshot } = useWorkflowRunStream(activeRunId);

  /* ── 3. Selected step ────────────────────────────────────────── */
  const [selectedStepId, setSelectedStepId] = useState<string>('');
  useEffect(() => {
    setSelectedStepId(graph?.nodes?.[0]?.id ?? '');
  }, [graph?.nodes?.[0]?.id]);

  /* ── 4. Status from snapshot ─────────────────────────────────── */
  const statusByStepId = useMemo(() => {
    const map: Record<string, string> = {};
    if (!runSnapshot || !Array.isArray(runSnapshot.steps)) return map;
    for (const s of runSnapshot.steps) {
      const id = String((s as any).step_id ?? '');
      if (!id) continue;
      map[id] = String((s as any).status ?? 'PENDING');
    }
    return map;
  }, [runSnapshot]);

  /* ── 5. Runtime data from snapshot ───────────────────────────── */
  const runtimeInputs = useMemo(() => {
    const r: any = (runSnapshot as any)?.run;
    return (r?.inputs ?? r?.Inputs ?? {}) as Record<string, any>;
  }, [runSnapshot]);

  const runtimeStepById = useMemo(() => {
    const map: Record<string, any> = {};
    const steps: any[] = Array.isArray((runSnapshot as any)?.steps) ? (runSnapshot as any).steps : [];
    for (const s of steps) {
      const id = String(s?.step_id ?? s?.StepID ?? '');
      if (!id) continue;
      map[id] = {
        status: s?.status ?? s?.Status,
        last_error: s?.last_error ?? s?.LastError,
        output: s?.output ?? s?.Output,
        job_id: s?.job_id ?? s?.jobId ?? s?.id_job ?? s?.JobID ?? s?.worker_job_id ?? s?.device_job_id,
        job_type: s?.job_type ?? s?.jobType ?? s?.JobType,
        attempts: (typeof s?.attempts === 'number' ? s.attempts : (typeof s?.Attempts === 'number' ? s.Attempts : null)),
        updated_at: s?.updated_at ?? s?.UpdatedAt,
      };
    }
    return map;
  }, [runSnapshot]);

  /* ── 6. Job progress stream ──────────────────────────────────── */
  const { events: jobProgressEvents } = useJobProgressStream(activeRunId, { fallbackPollMs: 3000, autoStopOnDone: false });

  const deriveStepId = (evt: any): string => {
    const direct = String(evt?.step_id ?? evt?.stepId ?? '').trim();
    if (direct) return direct;

    const jid = String(evt?.job_id ?? evt?.jobId ?? '').trim();
    if (!jid) return '';

    const rid = String(activeRunId ?? '').trim();
    if (!rid) return '';
    const prefix = `${rid}:`;
    if (jid.startsWith(prefix)) return jid.slice(prefix.length);

    const idx = jid.indexOf(':');
    if (idx <= 0 || idx >= jid.length - 1) return '';
    const maybe = jid.slice(idx + 1).trim();
    if (!maybe) return '';
    if (!/^[a-zA-Z0-9_-]+$/.test(maybe)) return '';
    return maybe;
  };

  /* ── 7. Progress maps ───────────────────────────────────────── */
  const stepProgressById = useMemo(() => {
    const map: Record<string, { pct?: number | null; message?: string | null }> = {};

    const isRefreshNoise = (pct: number | null, msg: unknown): boolean => {
      const m = (typeof msg === 'string' ? msg : '').trim().toLowerCase();
      if (!m) return false;
      if (!m.startsWith('refreshing')) return false;
      return pct == null || pct === 0;
    };

    for (const e of jobProgressEvents || []) {
      const anyE: any = e as any;
      const stepId = deriveStepId(anyE);
      if (!stepId) continue;

      const pct = extractPct(anyE);
      const msg = extractMsg(anyE);
      if (isRefreshNoise(pct, msg)) continue;
      map[stepId] = { pct, message: (typeof msg === 'string' ? msg : null) };
    }

    const steps: any[] = Array.isArray((runSnapshot as any)?.steps) ? (runSnapshot as any).steps : [];
    for (const s of steps) {
      const stepId = String(s?.step_id ?? s?.StepID ?? '').trim();
      if (!stepId || map[stepId]) continue;
      const p = s?.progress;
      if (!p) continue;
      const pct = extractPct(p);
      const msg = extractMsg(p);
      if (isRefreshNoise(pct, msg)) continue;
      map[stepId] = { pct, message: (typeof msg === 'string' ? msg : null) };
    }
    return map;
  }, [jobProgressEvents, runSnapshot, activeRunId]);

  const stepProgressLinesById = useMemo(() => {
    const map: Record<string, string[]> = {};

    const isNoise = (l: string) => {
      const lo = String(l || '').trim().toLowerCase();
      return Boolean(lo) && (lo === '0% refreshing' || lo.startsWith('0% refreshing') || lo.startsWith('refreshing'));
    };

    const push = (stepId: string, line: string) => {
      const s = String(stepId || '').trim();
      const l = String(line || '').trim();
      if (!s || !l || isNoise(l)) return;
      map[s] = [l];
    };

    for (const e of jobProgressEvents || []) {
      const anyE: any = e as any;
      const stepId = deriveStepId(anyE);
      if (!stepId) continue;
      const pct = extractPct(anyE);
      const msg = extractMsg(anyE);
      const m = typeof msg === 'string' ? msg.trim() : '';
      const line = pct != null && m ? `${pct}% ${m}` : pct != null ? `${pct}%` : m;
      if (line) push(stepId, line);
    }

    const steps: any[] = Array.isArray((runSnapshot as any)?.steps) ? (runSnapshot as any).steps : [];
    for (const s of steps) {
      const stepId = String(s?.step_id ?? s?.StepID ?? '').trim();
      if (!stepId) continue;
      const p: any = s?.progress;
      if (!p) continue;
      const lines = Array.isArray(p.lines) ? p.lines : Array.isArray(p.log_lines) ? p.log_lines : Array.isArray(p.logs) ? p.logs : null;
      if (!lines) continue;
      for (const raw of lines.slice(-8)) push(stepId, String(raw ?? '').trim());
    }
    return map;
  }, [jobProgressEvents, runSnapshot, activeRunId]);

  const stepProgressUpdatedAtMsById = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of jobProgressEvents || []) {
      const anyE: any = e as any;
      const stepId = deriveStepId(anyE);
      if (!stepId) continue;

      const raw = anyE.ts ?? anyE.time ?? anyE.at ?? anyE.created_at ?? anyE.updated_at ?? anyE.createdAt ?? anyE.updatedAt ?? anyE.t_ms ?? anyE.t;

      let ms: number | null = null;
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        ms = raw > 1_000_000_000_000 ? raw : Math.round(raw * 1000);
      } else if (typeof raw === 'string') {
        ms = parseDateMs(raw);
      }
      if (ms == null) ms = Date.now();
      map[stepId] = ms;
    }
    return map;
  }, [jobProgressEvents, activeRunId]);

  /* ── 8. Render ───────────────────────────────────────────────── */
  if (!graph || !graph.nodes.length) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-sm text-neutral-500">No graph available.</div>
      </div>
    );
  }

  return (
    <WorkflowGraphView
      nodes={graph.nodes}
      edges={graph.edges}
      layout={graph.layout}
      orientation="vertical"
      enableRunState={Boolean(activeRunId)}
      inputKeys={graph.inputKeys}
      inputMetaByKey={graph.inputMetaByKey}
      outputKeys={graph.outputKeys}
      outputMetaByKey={graph.outputMetaByKey}
      outputsBindingsJson={graph.outputsBindingsJson}
      selectedStepId={selectedStepId || graph.nodes[0]?.id || ''}
      statusByStepId={statusByStepId}
      runtimeInputs={runtimeInputs}
      runtimeStepById={runtimeStepById}
      stepProgressById={stepProgressById}
      stepProgressUpdatedAtMsById={stepProgressUpdatedAtMsById}
      stepProgressLinesById={stepProgressLinesById}
      onSelect={setSelectedStepId}
    />
  );
}

/* ── Local helpers ─────────────────────────────────────────────── */

function extractPct(obj: any): number | null {
  return (
    (typeof obj.pct === 'number' ? obj.pct : null)
    ?? (typeof obj.percent === 'number' ? obj.percent : null)
    ?? (typeof obj.progress === 'number' ? Math.round(obj.progress * 100) : null)
    ?? (typeof obj.stage_progress === 'number' ? Math.round(obj.stage_progress * 100) : null)
    ?? (typeof obj.overall_progress === 'number' ? Math.round(obj.overall_progress * 100) : null)
  );
}

function extractMsg(obj: any): string | null {
  return (
    (typeof obj.message === 'string' ? obj.message : null)
    ?? (typeof obj.stage === 'string' ? obj.stage : null)
    ?? (typeof obj.phase_name === 'string' ? obj.phase_name : null)
    ?? (typeof obj.phase === 'string' ? obj.phase : null)
  );
}

function parseDateMs(v: any): number | null {
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return null;
  const d = new Date(s);
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

function inferEdgesFromBindings(
  nodes: Array<{ id: string; paramsBindingsJson: string }>,
  existingEdges: Array<{ from: string; to: string }>,
) {
  const rx = /^\{\{\s*steps\.([a-zA-Z0-9_-]+)\.outputs\.([a-zA-Z0-9_-]+)\s*\}\}$/;
  const existing = new Set(existingEdges.map((e) => `${e.from}->${e.to}`));
  const nodeIds = new Set(nodes.map((n) => n.id));
  const inferred: Array<{ id: string; from: string; to: string }> = [];

  const walk = (v: any, cb: (s: string) => void): void => {
    if (typeof v === 'string') { cb(v); return; }
    if (!v) return;
    if (Array.isArray(v)) { v.forEach((x) => walk(x, cb)); return; }
    if (typeof v === 'object') Object.values(v).forEach((x) => walk(x, cb));
  };

  for (const n of nodes) {
    let obj: any = {};
    try { obj = JSON.parse(n.paramsBindingsJson || '{}'); } catch { obj = {}; }

    const deps = new Set<string>();
    walk(obj, (s) => {
      const m = String(s).trim().match(rx);
      if (!m) return;
      const parentId = m[1];
      if (!parentId || parentId === n.id || !nodeIds.has(parentId)) return;
      deps.add(parentId);
    });

    Array.from(deps).sort().forEach((from) => {
      const key = `${from}->${n.id}`;
      if (existing.has(key)) return;
      existing.add(key);
      inferred.push({ id: `inf_${from}_${n.id}`, from, to: n.id });
    });
  }

  return inferred;
}
